import React, { useCallback, useState } from "react";
import { Button, Header, Icon, Message, Segment } from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { functionSeedMortgageRatesFromCsv } from "../../app/firestore/functionsService";

// TEMPORARY admin tool for backfilling marketData/mortgage30yr from a FRED
// "30-Year Fixed Rate Mortgage Average" CSV export (2 columns: Date, rate).
// Delete this file, its import/render in AdminPage.jsx, the
// seedMortgageRatesFromCsv export in functions/index.js, and
// functionSeedMortgageRatesFromCsv in functionsService.js once the
// historical backfill is done - the weekly updateMortgageRates scheduled
// function is what keeps this data current going forward.

// CSV dates are M/D/YY (e.g. "9/10/26") - two-digit years assumed 2000s,
// correct for this series' entire range (2016-present).
function parseCsvDate(raw) {
  const parts = raw.trim().split("/").map(Number);
  if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return null;
  const [m, d, yRaw] = parts;
  const year = yRaw < 100 ? 2000 + yRaw : yRaw;
  return `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = String(e.target.result || "").replace(/^﻿/, "");
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) {
          reject(new Error("CSV must have a header row and at least one data row"));
          return;
        }
        const rows = lines.slice(1).map((line, idx) => {
          const [rawDate, rawRate] = line.split(",");
          const date = parseCsvDate(rawDate || "");
          const rate = parseFloat(rawRate);
          if (!date || !Number.isFinite(rate)) {
            console.warn(`Row ${idx + 2} skipped - could not parse "${line}"`);
            return null;
          }
          return { date, rate };
        }).filter(Boolean);
        if (rows.length === 0) {
          reject(new Error("No valid rows found - expected columns like \"9/10/26,6.76\""));
          return;
        }
        resolve(rows);
      } catch (err) {
        reject(new Error("Failed to parse CSV: " + err.message));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}

export default function MortgageRatesCsvUpload() {
  const [observations, setObservations] = useState([]);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }
    setResult(null);
    try {
      const rows = await parseCsvFile(file);
      setObservations(rows);
      setFileName(file.name);
      toast.success(`Parsed ${rows.length} row(s) from ${file.name}`);
    } catch (err) {
      toast.error(err.message);
      setObservations([]);
      setFileName("");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"], "application/vnd.ms-excel": [".csv"] },
    multiple: false,
  });

  async function handleUpload() {
    if (observations.length === 0) return;
    setUploading(true);
    try {
      const res = await functionSeedMortgageRatesFromCsv({ observations });
      setResult(res);
      toast.success(`Wrote ${res.updatedCount} observation(s). Latest: ${res.latest?.date} = ${res.latest?.rate}%`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  }

  const sorted = [...observations].sort((a, b) => (a.date < b.date ? -1 : 1));

  return (
    <Segment clearing>
      <Header size="huge" color="blue">
        30-Year Mortgage Rate CSV Upload (Temporary)
      </Header>
      <Message warning size="small">
        This is a one-time backfill tool - delete it once you've seeded historical rate data.
        Ongoing weekly updates run automatically (updateMortgageRates).
      </Message>
      <p>
        Upload a FRED "30-Year Fixed Rate Mortgage Average" CSV (2 columns: Date, rate) to backfill{" "}
        <code>marketData/mortgage30yr</code>.
      </p>
      <div
        {...getRootProps()}
        style={{
          border: "2px dashed #ccc",
          borderRadius: "6px",
          padding: "30px",
          textAlign: "center",
          cursor: "pointer",
          background: isDragActive ? "#f0f8ff" : "#fafafa",
        }}
      >
        <input {...getInputProps()} />
        <Icon name="file excel outline" size="big" color="grey" />
        <p style={{ marginTop: "10px" }}>
          {isDragActive ? "Drop the CSV here..." : "Drag & drop a CSV here, or click to select one"}
        </p>
      </div>

      {observations.length > 0 && (
        <Segment secondary style={{ marginTop: "16px" }}>
          <p style={{ margin: 0 }}>
            <strong>{fileName}</strong>: {observations.length} row(s) parsed, range{" "}
            {sorted[0].date} ({sorted[0].rate}%) to {sorted[sorted.length - 1].date} (
            {sorted[sorted.length - 1].rate}%)
          </p>
          <Button
            primary
            style={{ marginTop: "12px" }}
            loading={uploading}
            disabled={uploading}
            onClick={handleUpload}
          >
            <Icon name="upload" />
            Upload {observations.length} Row(s) to Firestore
          </Button>
        </Segment>
      )}

      {result && (
        <Message success size="small" style={{ marginTop: "12px" }}>
          Wrote {result.updatedCount} observation(s). Snapshot now shows {result.latest?.date} ={" "}
          {result.latest?.rate}%.
        </Message>
      )}
    </Segment>
  );
}
